import api, { API } from '@/utils/api.utils';

export interface FamilyMember {
  id?: string;
  relationship: 'mother' | 'father' | 'stepmother' | 'stepfather' | 'guardian' | 'sibling';
  living_status: 'living' | 'deceased';
  date_deceased?: string; // MM/YYYY
  
  // Name Information
  prefix?: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  former_last_name?: string;
  suffix?: string;
  
  // Professional Information
  occupation?: string;
  occupation_category?: string;
  occupation_original?: string;
  employer?: string;
  employment_status?: 'employed' | 'self_employed' | 'unemployed' | 'retired';
  
  // Education Information
  highest_education?: string;
  education_level?: 'none' | 'some_grade_primary' | 'completed_grade_primary' | 'some_high_secondary' | 'graduated_high_secondary' | 'some_trade_community' | 'graduated_trade_community' | 'some_college_university' | 'graduated_college_university' | 'graduate_degree';
  colleges?: College[];
  
  // Sibling specific
  age?: number;
  current_status?: 'high_school' | 'college' | 'working' | 'other';
}

export interface College {
  name: string;
  degree?: string;
  degree_category?: string;
  graduation_year?: number;
  field_of_study?: string;
}

export interface FamilyData {
  parents_relationship_status?: 'married' | 'divorced' | 'separated' | 'widowed' | 'never_married';
  household_composition?: string[];
  family_members?: FamilyMember[];
  siblings?: FamilyMember[];
  family_structure?: {
    parents_relationship_status?: 'married' | 'divorced' | 'separated' | 'widowed' | 'never_married';
    household_composition?: string[];
  };
  extraction_metadata?: {
    extracted_at?: string;
    original_description?: string;
    confidence_score?: number;
    last_updated?: string;
  };
}

export interface FamilyAnalysisResponse {
  family_data: FamilyData;
  missing_info: string[];
}

export const familyService = {
  // Analyze family description using AI
  async analyzeFamilyDescription(description: string): Promise<FamilyAnalysisResponse> {
    const response = await api.post('/api/v1/family/analyze', {
      description
    });
    return response.data;
  },

  // Get current family data
  async getFamilyData(): Promise<FamilyData> {
    const response = await api.get('/api/v1/family/data');
    return response.data;
  },

  // Update family data
  async updateFamilyData(familyData: FamilyData): Promise<void> {
    await api.put('/api/v1/family/data', {
      family_data: familyData
    });
  },

  // Complete family data with additional information
  async completeFamilyData(additionalInfo: string): Promise<FamilyAnalysisResponse> {
    const response = await api.post('/api/v1/family/complete', {
      additional_info: additionalInfo
    });
    return response.data;
  },

  // Get missing information suggestions
  async getMissingInfo(): Promise<string[]> {
    const response = await api.get('/api/v1/family/missing-info');
    return response.data;
  }
};

export default familyService;